import { parse as parseSDF } from 'sdf-parser';
import { PropApplicability } from 'BMapsSrc/model/ScopedPropertyGroup';
import { makeOneSDF } from 'BMapsUtil/mol_format_utils';
import { updateLocalJobState } from 'BMapsSrc/redux/jobs/access';
import { JobDefinition, JobResultDestinations } from '../JobDefinition';

// DrugFlow seeds `np.random.seed` directly with whatever it's given (see
// `utils.set_deterministic` in the pinned upstream source), which requires
// a value in [0, 2**32 - 1] — not an arbitrary UI choice, the actual hard
// limit of that call.
const MAX_SEED = 2 ** 32 - 1;

function randomSeed() {
    return Math.floor(Math.random() * (MAX_SEED + 1));
}

/**
 * DrugFlow structure-based de novo generation job: parses the generated
 * molecules from the result SDF into compound structures.
 *
 * Like GninaDockingJob, results are SDF molblocks (no SMILES column), so we
 * parse with sdf-parser and carry each molblock as the structure. SMILES is
 * derived on import when available.
 *
 * Owns `jobForm` (the field schema consumed by `JobForm`/`FieldList`) and
 * the submission methods below alongside its results/dispatch logic — data
 * and plain logic only, no rendering; `JobForm`/`FieldList` own all JSX.
 *
 * @augments JobDefinition
 */
export class DrugFlowJob extends JobDefinition {
    static jobTypeId = 'drugflow';
    static resultDestination = JobResultDestinations.COMPOUND;
    static jobTypeName = 'DrugFlow';
    static jobName = 'DrugFlow Job';

    /** Results rows keyed by their generated id. */
    static idField = 'id';

    /**
     * Declarative description of this job's form — data only, no rendering.
     * `body` is one of the named content regions `JobForm` renders (see
     * `formHelpers.js` for why they're named rather than an ordered list).
     * This job declares no `header`, and no submit label/tooltip/title —
     * the embedding tab supplies all of that (see `JobForm.jsx`'s
     * `mergeRegion` and its `footer` prop), since this job has no business
     * knowing what a program picker or job-naming field even are, or what
     * a given tab wants its submit button to say. `refLigandSpec` uses the
     * `pocket` widget — conceptually a compound, a hotspot, or the current
     * atom selection, unified the same way `DockingReference` in
     * `data_tools.js` already does elsewhere in the app, though today's
     * `pocket` widget only offers loaded compounds.
     * @type {object}
     */
    static jobForm = {
        body: {
            title: 'Specify Compound Input',
            fields: {
                refLigandSpec: {
                    widget: 'pocket',
                    label: 'Reference Ligand (defines pocket)',
                    default: '',
                    required: true,
                    requiredMessage: 'Select a reference ligand for DrugFlow generation',
                },
                nSamples: {
                    widget: 'number', label: 'Number of molecules', default: 10, min: 1,
                },
            },
            subsections: [
                {
                    title: 'Advanced Parameters',
                    collapsible: true,
                    fields: {
                        // A `variant`: which of these three kinds of value
                        // molecule_size holds determines what (if anything)
                        // else needs filling in. `fixed`/`range` each list
                        // their own sub-fields the same way this section's
                        // `fields` do. DrugFlow itself wants one string (a
                        // bare count or a "min,max" range), not the mode +
                        // sub-field(s) this variant produces — collapsing
                        // those into that one string happens explicitly in
                        // `buildRequestPayload`, since `deriveFormValues`
                        // maps one descriptor to one result key and can't
                        // combine several into one.
                        molecule_size: {
                            widget: 'variant',
                            label: 'Molecule size',
                            tooltip: 'How many atoms each generated molecule has — sampled automatically by the model, fixed to an exact size, or constrained to a size range.',
                            default: 'sampled',
                            options: {
                                sampled: { label: 'Default' },
                                fixed: {
                                    label: 'Fixed size',
                                    fields: {
                                        fixedSize: { widget: 'number', label: 'Size (atoms)', min: 1 },
                                    },
                                },
                                range: {
                                    label: 'Size range',
                                    fields: {
                                        minSize: { widget: 'number', label: 'Min size (atoms)', min: 1 },
                                        maxSize: { widget: 'number', label: 'Max size (atoms)', min: 1 },
                                    },
                                },
                            },
                        },
                        n_steps: {
                            widget: 'number',
                            // Kept short deliberately -- `label` also gets
                            // reused verbatim in generated required/bounds
                            // messages (see `formHelpers.js`'s
                            // `boundsMessage`), so a parenthetical aside
                            // here would show up mid-error-message too
                            // (e.g. "...(blank = model default) must be at
                            // least 1" reads like a contradiction). The
                            // aside belongs in the tooltip instead.
                            label: 'Denoising steps',
                            tooltip: 'Number of denoising steps the diffusion model takes to generate each molecule. More steps can improve sample quality at the cost of runtime. Leave blank to use the model\'s own default.',
                            min: 1,
                        },
                        seed: {
                            widget: 'number',
                            // See `n_steps` above for why this stays short.
                            label: 'Random seed',
                            tooltip: 'Random seed controlling generation stochasticity — set a fixed value for reproducible results, or leave blank for a new random seed each time.',
                            // Matches numpy's `np.random.seed` range (what
                            // DrugFlow itself seeds with) — the actual hard
                            // limit, not an arbitrary UI choice.
                            min: 0,
                            max: MAX_SEED,
                            // Deliberately no `fallback`: unlike batch_size/
                            // pocket_distance_cutoff below, there's no single
                            // number blank actually resolves to — it means a
                            // fresh `randomSeed()` every submission (see
                            // `buildRequestPayload`), so showing any one
                            // number as a placeholder would misstate that.
                        },
                        batch_size: {
                            widget: 'number',
                            label: 'Batch size',
                            tooltip: 'Number of molecules generated per batch. Higher values can improve throughput at the cost of memory use.',
                            min: 1,
                            default: 32,
                            fallback: 32,
                        },
                        pocket_distance_cutoff: {
                            widget: 'number',
                            label: 'Pocket distance cutoff (Å)',
                            tooltip: 'Maximum distance (Å) from the reference ligand used to define the binding-pocket residues considered during generation.',
                            min: 1,
                            default: 8.0,
                            fallback: 8.0,
                        },
                    },
                },
            ],
        },
    };

    /**
     * Assembles the DrugFlow request payload from `payload` — the flat
     * `property: value` object `JobForm` derives from `formState` via
     * `deriveFormValues` (already resolved and stripped of which
     * section/subsection a property came from — this method never has to
     * know the "Advanced Parameters" subsection existed). Every numeric
     * field here (`nSamples`, `fixedSize`/`minSize`/`maxSize`, `seed`, and
     * whatever rides through in `...rest` — `n_steps`, `batch_size`,
     * `pocket_distance_cutoff`) already arrives as an actual number, not a
     * string: `NumberWidget` (`widgets/simpleWidgets.jsx`) casts at the
     * source, in its own `setValue`, rather than leaving every consumer of
     * a number field to re-cast defensively — so this method needs no
     * `Number()` calls of its own. `refLigandSpec` arrives already resolved
     * (by the `pocket` widget's resolver in `widgets/index.js`) into
     * `{ proteinPdb, ligandSdf }`. `molecule_size` is the one thing this
     * method can't get generically at all: DrugFlow wants one string (a
     * bare count or a "min,max" range), not the separate mode + sub-
     * field(s) the `variant` widget produces, so collapsing those into
     * that one string happens explicitly here. `seed` is the other
     * exception: when the field is left blank, `payload.seed` is simply
     * absent (blank fields never reach `payload` at all — see
     * `formHelpers.js`'s `isEmpty` check), and omitting `--seed` entirely
     * doesn't give DrugFlow a random run — the pinned upstream script's own
     * `argparse` default (`--seed`, `default=42`) silently takes over, so
     * "leave it blank" would otherwise always reproduce the same run. A
     * fresh `randomSeed()` fills that gap so blank actually means random,
     * matching the field's own tooltip.
     * @param {object} payload
     * @returns {Promise<{ requestData?: object, error?: string[] }>}
     */
    static async buildRequestPayload(payload) {
        const {
            refLigandSpec: { proteinPdb, ligandSdf },
            nSamples, molecule_size: moleculeSizeMode, fixedSize, minSize, maxSize, seed,
            ...rest
        } = payload;

        // fixedSize/minSize/maxSize are the `molecule_size` variant's own
        // sub-fields, not top-level jobForm descriptors — they never pass
        // through formHelpers.js's generic min/max check (it only walks the
        // flat descriptor list), so their `min: 1` has to be enforced here.
        // `Number.isFinite` rather than bare falsiness or an `=== undefined`
        // check specifically: falsiness would treat a legitimate, explicitly-
        // typed 0 as if the field were empty (misreporting "Enter a fixed
        // molecule size" instead of "must be at least 1 atom" — the same
        // falsy-zero trap `deriveFormValues`'s own `isEmpty` exists to avoid
        // one level up); `=== undefined` alone would fix that but still let
        // NaN/Infinity (e.g. typing `1e400`, a syntactically valid number
        // literal) sneak past both this check AND the `< 1` check below
        // (`NaN < 1` and `Infinity < 1` are both `false`), silently landing
        // `String(Infinity)`/`String(NaN)` in the request with no error
        // surfaced at all. `Number.isFinite` rejects undefined, NaN, and
        // Infinity in one check.
        let moleculeSize;
        if (moleculeSizeMode === 'fixed') {
            if (!Number.isFinite(fixedSize)) return { error: 'Enter a fixed molecule size.' };
            if (fixedSize < 1) return { error: 'Molecule size must be at least 1 atom.' };
            moleculeSize = String(fixedSize);
        } else if (moleculeSizeMode === 'range') {
            if (!Number.isFinite(minSize) || !Number.isFinite(maxSize)) {
                return { error: 'Enter both minimum and maximum molecule sizes.' };
            }
            if (minSize < 1 || maxSize < 1) {
                return { error: 'Molecule size must be at least 1 atom.' };
            }
            if (minSize > maxSize) {
                return { error: 'Minimum molecule size cannot exceed maximum molecule size.' };
            }
            moleculeSize = `${minSize},${maxSize}`;
        }

        const requestData = {
            protein_pdb: proteinPdb,
            ref_ligand_sdf: ligandSdf,
            n_samples: nSamples,
            seed: seed ?? randomSeed(),
            ...rest,
            ...(moleculeSize !== undefined ? { molecule_size: moleculeSize } : {}),
        };

        return { requestData };
    }

    /**
     * Results-table columns for generated compounds.
     * @override
     * @returns {Object<string, import('../JobDefinition').DataInfoEntry>}
     */
    static getDataInfo() {
        return {
            SMILES: {
                label: 'Structure', structureValue: true, defaultVisible: true, propApplicability: PropApplicability.PERCOMPOUND,
            },
            name: {
                label: 'Name', type: 'string', defaultVisible: true, propApplicability: PropApplicability.PERCOMPOUND,
            },
        };
    }

    /**
     * Parses the DrugFlow SDF into generated structures and stores them on
     * local job state for the results table / import.
     * @override
     * @param {object} job
     * @param {object|string} results
     * @param {import('../JobDefinition').IntegrateResultsOptions} [_options]
     * @returns {Promise<import('../JobDefinition').IntegrateResultsReturn>}
     */
    async integrateResults(job, results, _options) {
        let resultsPacket = results;
        let structures = [];
        let error;
        try {
            if (!resultsPacket) return { error: 'Empty results' };
            if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
            if (!resultsPacket.jobResults?.sdf) return { error: 'Empty results' };
            const parsed = parseSDF(resultsPacket.jobResults.sdf);
            structures = parsed.molecules.map((entry, i) => {
                const name = `drugflow_${i + 1}`;
                const sdf = makeOneSDF(entry.molfile, { tmpSpec: name, tmpI: i });
                return {
                    id: `${job.jobid}_${i}`,
                    name,
                    SMILES: entry.smiles || null,
                    sdf,
                };
            });
            updateLocalJobState(job.jobid, { structures });
        } catch (ex) {
            error = `Error loading ${this.jobTypeId} results`;
            console.error(error, ex);
        }
        return {
            error,
            results: { structures, jobResults: resultsPacket.jobResults },
        };
    }
}
